import React, { useEffect, useMemo, useRef, useState } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    Avatar,
    Box,
    ClickAwayListener,
    Grid,
    List,
    ListItemButton,
    ListItemText,
    Paper,
    Popper,
    Typography,
    useMediaQuery
} from '@mui/material';

// project imports
import Transitions from 'ui-component/extended/Transitions';

// assets
import TranslateTwoToneIcon from '@mui/icons-material/TranslateTwoTone';
import useConfig from 'hooks/useConfig';
import { LANGUAGES } from 'constant';
import { LanguageOption } from 'types';
import { Trans } from 'react-i18next';

// ==============================|| LOCALIZATION ||============================== //

const LocalizationSection = () => {
    const { borderRadius, locale, onChangeLocale } = useConfig();

    const theme = useTheme();
    const matchesXs = useMediaQuery(theme.breakpoints.down('md'));

    const [open, setOpen] = useState<boolean>(false);
    const anchorRef = useRef<any>(null);
    const [language, setLanguage] = useState<LanguageOption>(() => {
        return LANGUAGES.filter((item) => item.key == locale)[0];
    });

    useEffect(() => {
        setLanguage(LANGUAGES.filter((item) => item.key == locale)[0]);
    }, [locale]);

    let handleLanguageChange = (lng: LanguageOption) => {
        onChangeLocale(lng);
        setOpen(false);
    };

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = (event: MouseEvent | TouchEvent) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        setOpen(false);
    };

    const prevOpen = useRef(open);
    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current.focus();
        }
        prevOpen.current = open;
    }, [open]);
    let languageOptions = useMemo(() => {
        return LANGUAGES.map((item) => (
            <ListItemButton key={item.key} selected={item.key == language.key} onClick={() => handleLanguageChange(item)}>
                <ListItemText
                    primary={
                        <Grid container>
                            <Typography color="textPrimary">
                                <Trans ns={['common']}>{item.name}</Trans>
                            </Typography>
                            <Typography variant="caption" color="textSecondary" sx={{ ml: '8px' }}>
                                ({item.key.toUpperCase()})
                            </Typography>
                        </Grid>
                    }
                />
            </ListItemButton>
        ));
    }, [LANGUAGES, locale, onChangeLocale]);
    if (LANGUAGES.length == 1) {
        return null;
    }

    return (
        <>
            <Box
                sx={{
                    ml: 0,
                    [theme.breakpoints.down('md')]: {
                        ml: 1
                    }
                }}
            >
                <Avatar
                    variant="rounded"
                    sx={{
                        ...theme.typography.commonAvatar,
                        ...theme.typography.mediumAvatar,
                        border: '1px solid',
                        borderColor: theme.palette.mode === 'dark' ? theme.palette.primary.dark : theme.palette.primary.light,
                        background: theme.palette.mode === 'dark' ? theme.palette.primary.dark : theme.palette.primary.light,
                        color: theme.palette.text.primary,
                        transition: 'all .2s ease-in-out',
                        '&[aria-controls="menu-list-grow"],&:hover': {
                            borderColor: theme.palette.primary.main,
                            background: theme.palette.primary.main,
                            color: theme.palette.primary.light
                        }
                    }}
                    ref={anchorRef}
                    aria-controls={open ? 'menu-list-grow' : undefined}
                    aria-haspopup="true"
                    onClick={handleToggle}
                    color="inherit"
                >
                    <Typography variant="h5" sx={{ textTransform: 'uppercase' }} color="inherit">
                        {language.key}
                    </Typography>
                </Avatar>
            </Box>
            <Popper
                placement={matchesXs ? 'bottom-start' : 'bottom'}
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                modifiers={[
                    {
                        name: 'offset',
                        options: {
                            offset: [matchesXs ? 0 : 0, 20]
                        }
                    }
                ]}
            >
                {({ TransitionProps }) => (
                    <ClickAwayListener onClickAway={handleClose}>
                        <Transitions position={matchesXs ? 'top-left' : 'top'} {...TransitionProps} in={open}>
                            <Paper elevation={16}>
                                {open && (
                                    <List
                                        component="nav"
                                        sx={{
                                            width: '100%',
                                            minWidth: 200,
                                            maxWidth: 280,
                                            bgcolor: theme.palette.background.paper,
                                            borderRadius: `${borderRadius}px`,
                                            [theme.breakpoints.down('md')]: {
                                                maxWidth: 250
                                            }
                                        }}
                                    >
                                        {languageOptions}
                                    </List>
                                )}
                            </Paper>
                        </Transitions>
                    </ClickAwayListener>
                )}
            </Popper>
        </>
    );
};

export default LocalizationSection;
